/**
 * Baymo: Klaviyo "back in stock" button on the PDP (snippets/baymo-back-in-stock).
 *
 * - On page load and on every variant change (Horizon `variant:update`) it checks whether
 *   the variant selected in the main product form is in stock, and shows the slot only
 *   when it is not.
 * - Klaviyo injects its button (#klaviyo-bis-button-container) in the first
 *   form[action*="/cart/add"] of the page, which here is a hidden quick-add form of the
 *   menu drawer. Wherever it lands, it is moved into the slot. Klaviyo skips the injection
 *   when that id already exists, so the moved button is reused.
 * - While Klaviyo's button is shown, the "Agotado" button is hidden, so there is only one
 *   button ([data-baymo-bis-active] on the buy buttons block). If Klaviyo never places its
 *   button, "Agotado" stays.
 * - Klaviyo finds the selected variant through the URL (?variant=), which Horizon updates
 *   on variant change. If the variant is sold out and Klaviyo has not placed its button
 *   after a while, the URL is re-applied so Klaviyo re-evaluates it.
 */
import { ThemeEvents } from '@theme/events';

const SLOT_SELECTOR = '[data-baymo-bis-slot]';
const KLAVIYO_CONTAINER_ID = 'klaviyo-bis-button-container';
const NUDGE_DELAY = 1500;

/** @type {Set<string>} variants for which Klaviyo was already nudged */
const nudged = new Set();
let nudgeTimer = 0;

/** The slot of the main product (first buy buttons block of the page) */
function mainSlot() {
  return /** @type {HTMLElement | null} */ (document.querySelector(`.baymo-buy-buttons ${SLOT_SELECTOR}`));
}

/** @param {HTMLElement} slot */
function readAvailability(slot) {
  try {
    return JSON.parse(slot.querySelector('script[data-baymo-bis-availability]')?.textContent || '{}');
  } catch {
    return {};
  }
}

/** @param {HTMLElement} slot */
function selectedVariantId(slot) {
  const buyButtons = slot.closest('.baymo-buy-buttons');
  const input = /** @type {HTMLInputElement | null} */ (buyButtons?.querySelector('input[name="id"]'));
  return input?.value || new URL(window.location.href).searchParams.get('variant') || '';
}

/** Moves Klaviyo's button into the slot, wherever Klaviyo injected it */
function adoptKlaviyoButton() {
  const slot = mainSlot();
  const container = document.getElementById(KLAVIYO_CONTAINER_ID);
  if (slot && container && !slot.contains(container)) slot.appendChild(container);
  syncActive();
}

/** Replaces "Agotado" with Klaviyo's button only when that button is actually in the slot */
function syncActive() {
  const slot = mainSlot();
  const buyButtons = slot?.closest('.baymo-buy-buttons');
  if (!slot || !(buyButtons instanceof HTMLElement)) return;
  const active = !slot.hidden && Boolean(slot.querySelector(`#${KLAVIYO_CONTAINER_ID}`));
  buyButtons.toggleAttribute('data-baymo-bis-active', active);
}

/** @param {string} variantId */
function nudgeKlaviyo(variantId) {
  window.clearTimeout(nudgeTimer);
  if (!variantId || nudged.has(variantId)) return;

  nudgeTimer = window.setTimeout(() => {
    const slot = mainSlot();
    if (!slot || slot.hidden || document.getElementById(KLAVIYO_CONTAINER_ID)) return;
    if (selectedVariantId(slot) !== variantId) return;

    nudged.add(variantId);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState(history.state, '', url.toString());
  }, NUDGE_DELAY);
}

/**
 * @param {{ id?: number|string, available?: boolean } | null} [variant] - From `variant:update`
 */
function update(variant) {
  const slot = mainSlot();
  if (!slot) return;

  const variantId = selectedVariantId(slot);
  let available = readAvailability(slot)[variantId];

  // The event carries fresher stock than the JSON rendered with the page
  if (variant && String(variant.id) === variantId && typeof variant.available === 'boolean') {
    available = variant.available;
  }

  // Unknown variant: keep it hidden rather than offering a wrong subscription
  const soldOut = available === false;
  slot.hidden = !soldOut;

  if (soldOut) {
    adoptKlaviyoButton();
    nudgeKlaviyo(variantId);
  } else {
    syncActive();
  }
}

// Klaviyo injects its button asynchronously (on load and when ?variant= changes)
// (and if it ever removes it, "Agotado" comes back)
/** @param {Node} node */
const isKlaviyoNode = (node) =>
  node instanceof HTMLElement &&
  (node.id === KLAVIYO_CONTAINER_ID || Boolean(node.querySelector?.(`#${KLAVIYO_CONTAINER_ID}`)));

new MutationObserver((records) => {
  for (const record of records) {
    if ([...record.addedNodes].some(isKlaviyoNode)) {
      adoptKlaviyoButton();
      return;
    }
    if ([...record.removedNodes].some(isKlaviyoNode)) {
      syncActive();
      return;
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
  // Variant pickers of product cards ("Completa tu look", quick add) are not the main product
  if (target?.closest('product-card, quick-add-dialog, .quick-add')) return;
  const variant = /** @type {any} */ (event).detail?.resource ?? null;
  // After Horizon has updated the main form's variant id
  setTimeout(() => update(variant), 0);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => update());
} else {
  update();
}
